import { createClient } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { createResilientAuthStorage } from './authStorage';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  if (import.meta.env.DEV) {
    console.warn(
      'Supabase credentials not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in your .env file.'
    );
  }
}

// Session storage that survives localStorage going blank mid-page (seen on iOS
// after the camera / photo picker). Without this, supabase-js silently sends
// requests with the anon key while the UI still shows the user as logged in,
// and every write fails RLS. See authStorage.ts for the full write-up.
const authStorage = createResilientAuthStorage({
  onFallback: (key) => {
    Sentry.captureMessage('auth-storage: served session from memory after localStorage read returned null', {
      level: 'warning',
      tags: { feature: 'auth-storage' },
      extra: {
        key,
        visibilityState: typeof document !== 'undefined' ? document.visibilityState : null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      },
    });
  },
});

// A removal that arrives as a `storage` event is a real sign-out (or token
// rotation) in another tab, so the memory copy must follow it. iOS blanking
// the store fires no event, which is what lets us tell the two cases apart.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => authStorage.handleStorageEvent(e));
}

// Note: For full type safety, generate types from your Supabase project:
// npx supabase gen types typescript --project-id YOUR_PROJECT_ID > src/types/supabase.ts
// Then update this file to use: createClient<Database>(...)

export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-key',
  {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      storage: authStorage,
    },
  }
);
